import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { Badge } from "../../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

// Message interface for the AlertsToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Alert data structure
interface Alert {
    uuid: string;
    name: string;
    description: string;
    status: string;
    destinations: string[];
    type: string;
    muted: boolean;
}

interface AlertsResponse {
    alerts: Alert[];
}

// Tool renderer for alerts data
export function AlertsToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // We only want to render the response, not the call
    if (!message.tool_call_response_content) {
        // This is a tool call, do the default rendering
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: AlertsResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing alerts response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.alerts || !Array.isArray(response.alerts)) {
        return null;
    }

    // Define columns for the DataTable
    const columns: ColumnDef<Alert>[] = [
        {
            id: "name",
            accessorKey: "name",
            header: "Name",
            cell: ({ row }) => {
                const name = row.original.name || `Unnamed Alert (${row.original.uuid.slice(0, 8)})`;
                return <div className="font-medium">{name}</div>;
            },
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const status = row.original.status;
                let colorClass = "";
                
                switch (status) {
                    case "OK":
                        colorClass = "bg-green-500/20 text-green-500 hover:bg-green-500/20";
                        break;
                    case "Warning":
                        colorClass = "bg-amber-500/20 text-amber-500 hover:bg-amber-500/20";
                        break;
                    case "Critical":
                        colorClass = "bg-red-500/20 text-red-500 hover:bg-red-500/20";
                        break;
                    default:
                        colorClass = "bg-blue-500/20 text-blue-500 hover:bg-blue-500/20";
                }
                
                return <Badge className={colorClass}>{status}</Badge>;
            },
        },
        {
            id: "type",
            accessorKey: "type",
            header: "Type",
            cell: ({ row }) => row.original.type,
        },
        {
            id: "description",
            accessorKey: "description",
            header: "Description",
            cell: ({ row }) => {
                const description = row.original.description || "-";
                // Truncate long descriptions
                return description.length > 50 
                    ? <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="truncate max-w-[200px]">{description.slice(0, 50)}...</div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    : <div>{description}</div>;
            },
        },
        {
            id: "muted",
            accessorKey: "muted",
            header: "Muted",
            cell: ({ row }) => {
                const isMuted = row.original.muted;
                return (
                    <Badge 
                        className={
                            isMuted
                                ? "bg-amber-500/20 text-amber-500 hover:bg-amber-500/20" 
                                : "bg-green-500/20 text-green-500 hover:bg-green-500/20"
                        }
                    >
                        {isMuted ? "Muted" : "Unmuted"}
                    </Badge>
                );
            },
        }
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full border rounded">
            <DataTable
                columns={columns}
                data={response.alerts}
                tableTitle="Alerts"
                filters={filters}
                showPagination={true}
                removeBorder={true}
                textFilteringColumnName="name"
                textFilteringPlaceholder="Search alerts..."
                columnConfigs={[
                    { id: "name", isFilterable: true },
                    { id: "type", isFilterable: true }
                ]}
            />
        </div>
    );
}

// Static method to determine if this renderer should render a message
AlertsToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};